import Phaser from 'phaser'
import { setConnectionStatus, ConnectionStatus } from '../stores/UserStore'
import store from '../stores'
import { setLoadingProgress } from '../stores/GameStore'
import { phaserEvents, Event } from '../events/EventCenter'
import Network from '../services/Network'
import Game from '../scenes/Game'

// Define initialization state flags
export enum InitStatus {
    PENDING = 'pending',
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'completed',
    FAILED = 'failed'
}

// Interface for initialization state
export interface InitializationState {
    authentication: InitStatus;
    databaseSync: InitStatus;
    playerData: InitStatus;
    webRTCInit: InitStatus;
    characterInit: InitStatus;
    proximityAreasEnabled: InitStatus;
    callsEnabled: InitStatus;
}

// Extend the Event enum with our new event
declare module '../events/EventCenter' {
    export enum Event {
        CHARACTER_INITIALIZED = 'character_initialized'
    }
}

// Define our custom event
const CHARACTER_INITIALIZED = 'character_initialized';

export class InitializationManager {
    private static instance: InitializationManager;
    private network: Network;
    private game: Game | null = null;

    // Initialization state
    private state: InitializationState = {
        authentication: InitStatus.PENDING,
        databaseSync: InitStatus.PENDING,
        playerData: InitStatus.PENDING,
        webRTCInit: InitStatus.PENDING,
        characterInit: InitStatus.PENDING,
        proximityAreasEnabled: InitStatus.PENDING,
        callsEnabled: InitStatus.PENDING
    };

    // Track current step for progress reporting
    private currentStep = 0;
    private totalSteps = 8; // Total number of initialization steps

    private constructor(network: Network) {
        this.network = network;

        // Register listeners for initialization events
        phaserEvents.on(Event.MY_PLAYER_READY, this.onPlayerReady, this);
    }

    // Get singleton instance
    public static getInstance(network?: Network): InitializationManager {
        if (!InitializationManager.instance && network) {
            InitializationManager.instance = new InitializationManager(network);
        }

        if (!InitializationManager.instance) {
            throw new Error('InitializationManager not initialized with Network');
        }

        return InitializationManager.instance;
    }

    // Set game scene reference
    public setGameScene(game: Game): void {
        this.game = game;
    }

    // Get the current initialization state
    public getState(): InitializationState {
        return { ...this.state };
    }

    // Check if all initialization steps are complete
    public isFullyInitialized(): boolean {
        return Object.values(this.state).every(status => status === InitStatus.COMPLETED);
    }

    // Start the initialization sequence
    public async startInitialization(): Promise<void> {
        console.log('[InitManager] Starting initialization sequence');
        store.dispatch(setConnectionStatus(ConnectionStatus.LOADING));

        try {
            // 1. Authentication (should already be completed before this is called)
            this.updateStatus('authentication', InitStatus.COMPLETED);

            // 2. Main Menu (not tracked, happens at UI level)

            // 3. Database Sync & Player Data (we'll do this together)
            await this.initializeDatabaseAndPlayerData();


            // 5. WebRTC initialization
            await this.initializeWebRTC();

            // 6. Character initialization
            this.initializeCharacter();

            // 7. Enable proximity areas
            this.enableProximityAreas();

            // 8. Enable calls and proximity events
            this.enableCallsAndProximityEvents();

            console.log('[InitManager] Initialization sequence completed successfully');
            store.dispatch(setConnectionStatus(ConnectionStatus.ONLINE));
        } catch (error) {
            console.error('[InitManager] Initialization failed:', error);
            store.dispatch(setConnectionStatus(ConnectionStatus.OFFLINE));
        }
    }

    // Handle player ready event
    private onPlayerReady(sessionId: string): void {
        console.log(`[InitManager] Player ready event received for session ${sessionId}`);
        // Start initialization if it hasn't started yet
        if (this.state.playerData === InitStatus.PENDING) {
            this.startInitialization();
        }
    }

    // Update initialization status and progress
    private updateStatus(step: keyof InitializationState, status: InitStatus): void {
        this.state[step] = status;

        if (status === InitStatus.COMPLETED) {
            this.currentStep++;
            const progress = Math.floor((this.currentStep / this.totalSteps) * 100);
            store.dispatch(setLoadingProgress(progress));
            console.log(`[InitManager] Step ${step} completed. Progress: ${progress}%`);
        } else if (status === InitStatus.IN_PROGRESS) {
            console.log(`[InitManager] Step ${step} in progress...`);
        }
    }

    // Initialize database and player data
    private async initializeDatabaseAndPlayerData(): Promise<void> {
        try {
            this.updateStatus('databaseSync', InitStatus.IN_PROGRESS);
            this.updateStatus('playerData', InitStatus.IN_PROGRESS);

            // Request initial room state and player info
            await this.network.requestRoomState(true);
            await this.network.requestPlayerInfo();
            await this.network.broadcastPlayerInfo();

            this.updateStatus('databaseSync', InitStatus.COMPLETED);
            this.updateStatus('playerData', InitStatus.COMPLETED);
        } catch (error) {
            console.error('[InitManager] Database and player data initialization failed:', error);
            this.updateStatus('databaseSync', InitStatus.FAILED);
            this.updateStatus('playerData', InitStatus.FAILED);
            throw error;
        }
    }

    // Initialize WebRTC if not already initialized
    private async initializeWebRTC(): Promise<void> {
        try {
            this.updateStatus('webRTCInit', InitStatus.IN_PROGRESS);

            console.log('[InitManager] WebRTC has been removed from the codebase');

            this.updateStatus('webRTCInit', InitStatus.COMPLETED);
        } catch (error) {
            console.error('[InitManager] Error in WebRTC initialization:', error);
            this.updateStatus('webRTCInit', InitStatus.FAILED);
            throw error;
        }
    }

    // Initialize character after WebRTC is ready
    private initializeCharacter(): void {
        try {
            this.updateStatus('characterInit', InitStatus.IN_PROGRESS);

            if (this.game && this.game.myPlayer) {
                // Signal that the player is ready for visual display
                phaserEvents.emit(CHARACTER_INITIALIZED, this.game.myPlayer.playerId);
            }

            this.updateStatus('characterInit', InitStatus.COMPLETED);
        } catch (error) {
            console.error('[InitManager] Character initialization failed:', error);
            this.updateStatus('characterInit', InitStatus.FAILED);
            throw error;
        }
    }

    // Enable proximity areas with previously downloaded data
    private enableProximityAreas(): void {
        try {
            this.updateStatus('proximityAreasEnabled', InitStatus.IN_PROGRESS);

            console.log('[InitManager] Proximity areas are not available');

            this.updateStatus('proximityAreasEnabled', InitStatus.COMPLETED);
        } catch (error) {
            console.error('[InitManager] Enabling proximity areas failed:', error);
            this.updateStatus('proximityAreasEnabled', InitStatus.FAILED);
            // Not throwing error here as we can continue without proximity areas
        }
    }

    // Enable calls and proximity events
    private enableCallsAndProximityEvents(): void {
        try {
            this.updateStatus('callsEnabled', InitStatus.IN_PROGRESS);

            console.log('[InitManager] Calls and proximity events are not available');

            if (this.game && this.game.network) {
                // Finalize the player connection
                this.game.finalizeConnection();
            }

            this.updateStatus('callsEnabled', InitStatus.COMPLETED);
        } catch (error) {
            console.error('[InitManager] Enabling calls and proximity events failed:', error);
            this.updateStatus('callsEnabled', InitStatus.FAILED);
            // Not throwing error here as we can continue without calls
        }
    }
}

// Export the character initialized event name for use elsewhere
export { CHARACTER_INITIALIZED }; 